import { z } from 'zod';

/**
 * Failure Intelligence — a failed execution becomes a Failure Record,
 * not a Jira ticket. Classification, similar-failure linking, and a
 * local defect draft live here. codeChanges is true only from owned
 * TIA file lists. Logs / deploys stay unwired. No confidence score.
 */

export const FAILURE_TYPES = [
  'product_defect',
  'test_defect',
  'environment_issue',
  'test_data_issue',
  'configuration_issue',
  'dependency_issue',
  'infrastructure_issue',
  'unknown',
] as const;
export type FailureType = (typeof FAILURE_TYPES)[number];

export const FAILURE_STATUSES = ['open', 'linked', 'resolved'] as const;
export type FailureStatus = (typeof FAILURE_STATUSES)[number];

/** Feature 3A — banded confidence for historical resolution memory (not percent certainty). */
export const RESOLUTION_MEMORY_CONFIDENCE = ['low', 'medium', 'high', 'insufficient'] as const;
export type ResolutionMemoryConfidence = (typeof RESOLUTION_MEMORY_CONFIDENCE)[number];

export const RESOLUTION_MEMORY_STATUSES = ['none', 'unverified', 'verified', 'insufficient'] as const;
export type ResolutionMemoryStatus = (typeof RESOLUTION_MEMORY_STATUSES)[number];

/**
 * Feature 7E — resolution evidence derived from an already-linked PM issue.
 *
 * `verified` is never reached by issue closure alone: it requires the existing
 * independent close mechanism on the Failure Record (root cause + verified_by +
 * fixed_build). A closed issue on its own is `unverified`; a link whose state
 * the provider does not expose is `limited`.
 */
export const RESOLUTION_EVIDENCE_STATUSES = ['none', 'limited', 'unverified', 'verified'] as const;
export type ResolutionEvidenceStatus = (typeof RESOLUTION_EVIDENCE_STATUSES)[number];

export const RESOLUTION_EVIDENCE_SOURCES = [
  'no_issue_link',
  'issue_open',
  'issue_closed',
  'issue_closed_by_passing_runs',
  'issue_state_unknown',
] as const;
export type ResolutionEvidenceSource = (typeof RESOLUTION_EVIDENCE_SOURCES)[number];

export const LINKED_ISSUE_STATUSES = ['open', 'closed', 'resolved-by-pass', 'unknown'] as const;
export type LinkedIssueStatus = (typeof LINKED_ISSUE_STATUSES)[number];

/** One row of an existing per-vendor issue-link table, normalised. */
export const LinkedIssueSchema = z.object({
  provider: z.string().max(40),
  issueId: z.string().max(200),
  identifier: z.string().max(200),
  url: z.string().max(500).nullable(),
  stateName: z.string().max(120),
  linkStatus: z.enum(LINKED_ISSUE_STATUSES),
  closedAt: z.string().nullable(),
  /** False when the provider's link table has no close timestamp (e.g. Jira). */
  closedAtKnown: z.boolean(),
});
export type LinkedIssue = z.infer<typeof LinkedIssueSchema>;

export const ResolutionEvidenceSchema = z.object({
  status: z.enum(RESOLUTION_EVIDENCE_STATUSES),
  source: z.enum(RESOLUTION_EVIDENCE_SOURCES),
  confidence: z.enum(RESOLUTION_MEMORY_CONFIDENCE),
  provider: z.string().max(40).nullable(),
  issueId: z.string().max(200).nullable(),
  issueIdentifier: z.string().max(200).nullable(),
  issueUrl: z.string().max(500).nullable(),
  closedAt: z.string().nullable(),
  /** Copied from the Failure Record's existing close field — never inferred from the issue. */
  verifiedBy: z.string().max(120),
  issues: z.array(LinkedIssueSchema).max(5),
  why: z.array(z.string().max(300)).max(8),
  governance: z.object({
    autoApply: z.literal(false),
    silentPublish: z.literal(false),
    recommendationsAreNotTruth: z.literal(true),
  }),
});
export type ResolutionEvidence = z.infer<typeof ResolutionEvidenceSchema>;

export const FailureResolutionMemoryItemSchema = z.object({
  id: z.string().uuid(),
  displayKey: z.string(),
  scenarioTitle: z.string(),
  failureType: z.enum(FAILURE_TYPES).nullable(),
  /** Derived from failure_type — not a second taxonomy store. */
  category: z.string().max(40),
  rootCause: z.string().max(500),
  verifiedBy: z.string().max(120),
  fixedBuild: z.string().max(80),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
  verification: z.enum(['verified', 'unverified']),
  evidence: z.array(z.string().max(200)).max(10),
  deepLink: z.string().max(500),
  durationHours: z.number().nullable(),
  /** Feature 7E — linked-issue evidence for this historical row, when a link exists. */
  resolutionEvidence: ResolutionEvidenceSchema.optional(),
});
export type FailureResolutionMemoryItem = z.infer<typeof FailureResolutionMemoryItemSchema>;

/**
 * Feature 3A — compose-on-read historical resolutions for the same fingerprint
 * (project-scoped). Recommendations only; never auto-apply.
 */
export const FailureResolutionMemorySchema = z.object({
  status: z.enum(RESOLUTION_MEMORY_STATUSES),
  confidence: z.enum(RESOLUTION_MEMORY_CONFIDENCE),
  similarResolvedCount: z.number().int().nonnegative(),
  /** Bounded open+resolved similar hint — not an unbounded project scan. */
  occurrenceHint: z.number().int().nonnegative(),
  summary: z.string().max(500),
  why: z.array(z.string().max(300)).max(12),
  items: z.array(FailureResolutionMemoryItemSchema).max(10),
  governance: z.object({
    autoApply: z.literal(false),
    silentPublish: z.literal(false),
    recommendationsAreNotTruth: z.literal(true),
  }),
});
export type FailureResolutionMemory = z.infer<typeof FailureResolutionMemorySchema>;

export const DEFECT_STATUSES = ['open', 'resolved'] as const;
export type DefectStatus = (typeof DEFECT_STATUSES)[number];

export const QualityDefectSchema = z.object({
  id: z.string().uuid(),
  displayKey: z.string(),
  title: z.string(),
  description: z.string(),
  steps: z.string(),
  expected: z.string(),
  actual: z.string(),
  environment: z.string(),
  build: z.string(),
  evidenceCount: z.number().int().nonnegative(),
  scenarioId: z.string().uuid().nullable(),
  scenarioTitle: z.string(),
  status: z.enum(DEFECT_STATUSES),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type QualityDefect = z.infer<typeof QualityDefectSchema>;

export const QualityDefectSummarySchema = z.object({
  id: z.string().uuid(),
  displayKey: z.string(),
  title: z.string(),
  status: z.enum(DEFECT_STATUSES),
});
export type QualityDefectSummary = z.infer<typeof QualityDefectSummarySchema>;

export const FailureEvidenceKindSchema = z.object({
  kind: z.enum(['screenshot', 'video', 'file']),
  name: z.string(),
});
export type FailureEvidenceKind = z.infer<typeof FailureEvidenceKindSchema>;

export const ScenarioExecutionHistoryItemSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(['in_progress', 'passed', 'failed', 'blocked', 'skipped']),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  build: z.string().default(''),
});
export type ScenarioExecutionHistoryItem = z.infer<typeof ScenarioExecutionHistoryItemSchema>;

export const FailureLastPassedSchema = z.object({
  at: z.string(),
  build: z.string(),
  commitSha: z.string().nullable(),
  source: z.enum(['manual', 'automation']),
});
export type FailureLastPassed = z.infer<typeof FailureLastPassedSchema>;

export const FailureFlakeHypothesisSchema = z.object({
  text: z.string(),
  category: z.string(),
  flowName: z.string(),
});
export type FailureFlakeHypothesis = z.infer<typeof FailureFlakeHypothesisSchema>;

export const FailureSiblingScenarioSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
});
export type FailureSiblingScenario = z.infer<typeof FailureSiblingScenarioSchema>;

export const FailureSignalsAvailableSchema = z.object({
  previousExecutions: z.boolean(),
  similarFailures: z.boolean(),
  knownDefects: z.boolean(),
  /** True only when `tia_commit_index.files` has paths for a linked automation commit. */
  codeChanges: z.boolean(),
  applicationLogs: z.literal(false),
  apiResponses: z.literal(false),
  networkErrors: z.literal(false),
  deployments: z.literal(false),
  /** Feature 3A — prior resolved rows for this fingerprint. */
  historicalResolutions: z.boolean().optional(),
  /** Feature 7E — a PM issue is linked to this failure's run. */
  linkedIssues: z.boolean().optional(),
});
export type FailureSignalsAvailable = z.infer<typeof FailureSignalsAvailableSchema>;

export const FailureAnalysisSchema = z.object({
  previousExecutions: z.array(ScenarioExecutionHistoryItemSchema),
  similarOpenCount: z.number().int().nonnegative(),
  similarFailures: z.array(
    z.object({
      id: z.string().uuid(),
      displayKey: z.string(),
      scenarioTitle: z.string(),
      createdAt: z.string(),
      defect: QualityDefectSummarySchema.nullable(),
    }),
  ),
  linkedDefect: QualityDefectSummarySchema.nullable(),
  lastPassed: FailureLastPassedSchema.nullable(),
  flakeHypothesis: FailureFlakeHypothesisSchema.nullable(),
  /** Linked automation commit SHA when Rynora owns one. Used to load TIA / VCS compare. */
  commitSha: z.string().nullable(),
  changedFiles: z.array(z.string()),
  siblingScenarios: z.array(FailureSiblingScenarioSchema),
  /** True when a passing re-execution exists after this failure was created. */
  canClose: z.boolean(),
  signalsAvailable: FailureSignalsAvailableSchema,
  /** Feature 3A — prior fingerprint-matched resolutions (project-scoped). */
  resolutionMemory: FailureResolutionMemorySchema.optional(),
  /** Feature 7E — evidence from a PM issue already linked to this failure's run. */
  resolutionEvidence: ResolutionEvidenceSchema.optional(),
});
export type FailureAnalysis = z.infer<typeof FailureAnalysisSchema>;

export function emptyFailureAnalysis(): FailureAnalysis {
  return {
    previousExecutions: [],
    similarOpenCount: 0,
    similarFailures: [],
    linkedDefect: null,
    lastPassed: null,
    flakeHypothesis: null,
    commitSha: null,
    changedFiles: [],
    siblingScenarios: [],
    canClose: false,
    signalsAvailable: {
      previousExecutions: false,
      similarFailures: false,
      knownDefects: false,
      codeChanges: false,
      applicationLogs: false,
      apiResponses: false,
      networkErrors: false,
      deployments: false,
      historicalResolutions: false,
      linkedIssues: false,
    },
    resolutionEvidence: {
      status: 'none',
      source: 'no_issue_link',
      confidence: 'insufficient',
      provider: null,
      issueId: null,
      issueIdentifier: null,
      issueUrl: null,
      closedAt: null,
      verifiedBy: '',
      issues: [],
      why: ['No issue is linked to this failure.'],
      governance: {
        autoApply: false,
        silentPublish: false,
        recommendationsAreNotTruth: true,
      },
    },
    resolutionMemory: {
      status: 'none',
      confidence: 'insufficient',
      similarResolvedCount: 0,
      occurrenceHint: 0,
      summary: 'No verified historical resolution found.',
      why: ['No fingerprint-matched historical resolutions in this project.'],
      items: [],
      governance: {
        autoApply: false,
        silentPublish: false,
        recommendationsAreNotTruth: true,
      },
    },
  };
}

export const QualityFailureSchema = z.object({
  id: z.string().uuid(),
  displayKey: z.string(),
  executionId: z.string().uuid(),
  /** Null when Failure Intelligence came from an Automation-only Flow (no Scenario link). */
  scenarioId: z.string().uuid().nullable(),
  scenarioTitle: z.string(),
  stepOrder: z.number().int().positive(),
  stepAction: z.string(),
  expected: z.string(),
  actual: z.string(),
  whatHappened: z.string(),
  environment: z.string(),
  browser: z.string(),
  build: z.string(),
  evidence: z.array(FailureEvidenceKindSchema),
  failureType: z.enum(FAILURE_TYPES).nullable(),
  status: z.enum(FAILURE_STATUSES),
  rootCause: z.string(),
  verifiedBy: z.string(),
  fixedBuild: z.string(),
  defect: QualityDefectSummarySchema.nullable(),
  similarOpenCount: z.number().int().nonnegative(),
  analysis: FailureAnalysisSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  resolvedAt: z.string().nullable(),
  /** Source automation run when failure came from linked automation execution. */
  sourceRunId: z.string().uuid().nullable().optional(),
  automationFlowId: z.string().uuid().nullable().optional(),
  automationFlowName: z.string().nullable().optional(),
});
export type QualityFailure = z.infer<typeof QualityFailureSchema>;

export const FailureListResponseSchema = z.object({
  projectId: z.string().uuid(),
  failures: z.array(QualityFailureSchema),
});
export type FailureListResponse = z.infer<typeof FailureListResponseSchema>;

export const FailureResponseSchema = z.object({
  failure: QualityFailureSchema,
});
export type FailureResponse = z.infer<typeof FailureResponseSchema>;

export const PatchFailureRequestSchema = z.object({
  failureType: z.enum(FAILURE_TYPES).optional(),
  status: z.enum(FAILURE_STATUSES).optional(),
  rootCause: z.string().max(2_000).optional(),
  verifiedBy: z.string().max(120).optional(),
  fixedBuild: z.string().max(80).optional(),
});
export type PatchFailureRequest = z.infer<typeof PatchFailureRequestSchema>;

export const LinkFailureRequestSchema = z.object({
  defectId: z.string().uuid(),
});
export type LinkFailureRequest = z.infer<typeof LinkFailureRequestSchema>;

export const DefectResponseSchema = z.object({
  defect: QualityDefectSchema,
  failure: QualityFailureSchema,
});
export type DefectResponse = z.infer<typeof DefectResponseSchema>;

/** Failures that share environment + build across scenarios, inside this window. */
export const ENVIRONMENTAL_CLUSTER_WINDOW_HOURS = 24;

export const EnvironmentalClusterSchema = z.object({
  environment: z.string(),
  build: z.string(),
  failureCount: z.number().int().nonnegative(),
  scenarioCount: z.number().int().nonnegative(),
  executionIds: z.array(z.string().uuid()),
  failureIds: z.array(z.string().uuid()),
});
export type EnvironmentalCluster = z.infer<typeof EnvironmentalClusterSchema>;

export const EnvironmentalClustersResponseSchema = z.object({
  projectId: z.string().uuid(),
  clusters: z.array(EnvironmentalClusterSchema),
});
export type EnvironmentalClustersResponse = z.infer<typeof EnvironmentalClustersResponseSchema>;

export const BlockEnvironmentalRequestSchema = z.object({
  environment: z.string().min(1).max(80),
  build: z.string().min(1).max(80),
  comment: z.string().max(4_000).optional(),
});
export type BlockEnvironmentalRequest = z.infer<typeof BlockEnvironmentalRequestSchema>;

export const BlockEnvironmentalResponseSchema = z.object({
  blocked: z.number().int().nonnegative(),
  resolved: z.number().int().nonnegative(),
});
export type BlockEnvironmentalResponse = z.infer<typeof BlockEnvironmentalResponseSchema>;
